import { mulBp, withinRange } from '../math/fixed.ts';
import { touchGrid } from '../grid.ts';
import type { BattleState, Enemy, SendaNpc, ShootRules, WorkshopRules, WorkshopState } from '../types.ts';
import { damageEnemy, ev } from './combat.ts';
import { golemOf, golemPost, golemsUp, golemTick, golemWake } from './golems.ts';

/**
 * The sleeping workshop (docs/sendas/artifice.md, chapter 1, act 2, «El taller dormido»; the ground in
 * battle/taller-layout.ts). Down through five halls cut in the rock, cold forges, chains, Nadd's old golems at their
 * posts (battle/golems.ts), to the lift's landing. A golem woken in a hall drops the grille of its stair down, and it
 * stays down while one of that hall's golems stands (awake, or out with its boiler open), and so does the one it came
 * in by (no running back up the stair): as in a crypt, what woke is
 * put down before going on; what slept can be walked past. The grille's clang wakes the rest of its hall, one every
 * `chain`. Dunna follows with her crossbow and shoots the nearest
 * awake (`dunna`); a bolt from behind opens a boiler like any blow. Won with the hero at the lift, Dunna with it.
 *
 * Cost: the golems' (battle/golems.ts); the grilles are the grid's plugs, changed only when one drops or lifts.
 */

const fresh = (r: WorkshopRules): WorkshopState => ({ hall: 0, shut: r.halls.map(() => false), woke: r.halls.map(() => 0), swing: 0, low: false });

const dunnaOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0);

/**
 * A path's person with a crossbow (Dunna): every `every`, a bolt at the nearest awake nightmare within `range` (an
 * asleep golem is iron: not that); from where it stands, so from behind it opens a boiler. Returns its next bolt.
 */
export const shootTick = (s: BattleState, n: SendaNpc, r: ShootRules, swing: number): number => {
  if (s.tick < swing || n.flinch > 0) return swing;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of s.enemies) {
    if (e.hp <= 0 || !withinRange(e.x, e.y, n.x, n.y, r.range)) continue;
    const g = golemOf(s, e.id);
    if (g && (g.mode === 0 || g.mode === 3)) continue;
    const d = Math.abs(e.x - n.x) + Math.abs(e.y - n.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  if (!best) return swing;
  ev(s, { t: 'senda', kind: 'bolt', npc: n.id, x: best.x, y: best.y });
  damageEnemy(s, best, r.hit, { pid: '', pierce: true, fromX: n.x, fromY: n.y });
  return s.tick + r.every;
};

/** The grilles down (their stairs' cells), as the grid's plugs, sorted. */
const plugs = (r: WorkshopRules, w: WorkshopState): number[] => {
  const out: number[] = [];
  r.halls.forEach((h, i) => {
    if (w.shut[i]) out.push(...h.stair);
  });
  return out.sort((a, b) => a - b);
};

export const workshopTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.workshop;
  const gr = s.rules?.senda?.golem;
  const st = s.senda;
  if (!r || !gr || !st) return;
  if (!st.workshop) {
    st.workshop = fresh(r);
    st.golem = { list: [], pend: [] };
    r.halls.forEach((h, i) => {
      for (const p of h.posts) golemPost(s, gr, p, i);
    });
  }
  const w = st.workshop;
  if (!s.started) return;
  golemTick(s, cue);
  // The hero's hall (by the nearest middle row), a cue on each one reached.
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h) {
    let best = 0;
    let bd = 0x7fffffff;
    r.halls.forEach((hl, i) => {
      const d = Math.abs(hl.at.y - h.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    if (best > w.hall) {
      w.hall = best;
      cue(`hall${best + 1}`);
    }
    if (!w.low && h.hp * 2 < h.stats.maxHp) {
      w.low = true;
      cue('low');
    }
  }
  // One golem awake wakes its hall a while later (the clang of the grille): the others light up one by one.
  for (const g of st.golem?.list ?? []) {
    if (g.mode !== 0 || g.hall < 0 || !w.shut[g.hall]) continue;
    if (s.tick >= (w.woke[g.hall] ?? 0)) {
      golemWake(s, g, cue);
      w.woke[g.hall] = s.tick + r.chain;
    }
  }
  // Each grille: down while a golem of its hall stands.
  let changed = false;
  // (A hall's golems up shut its stair down and, once the hero is down in it, the one it came in by: the way back is
  // cut too. Only then: golems woken below while the hero was still above would have shut it out for good.)
  r.halls.forEach((hl, i) => {
    if (!hl.stair.length) return;
    const shut = golemsUp(s, i) > 0 || (golemsUp(s, i + 1) > 0 && w.hall > i);
    if (shut === w.shut[i]) return;
    w.shut[i] = shut;
    if (shut) w.woke[i] = s.tick + r.chain;
    changed = true;
    const c = hl.stair[0] as number;
    void c;
    ev(s, { t: 'senda', kind: 'grille', npc: '', x: hl.at.x, y: hl.at.y, n: i * 2 + (shut ? 1 : 0) });
    if (shut) cue(`grille${i + 1}`);
  });
  if (changed) {
    s.plugs = plugs(r, w);
    touchGrid(s);
  }
  const d = dunnaOf(s);
  if (d) w.swing = shootTick(s, d, r.dunna, w.swing);
};

/** Halls gone down through (of five). */
export const workshopGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.workshop;
  const w = s.senda?.workshop;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.hall + (workshopResult(s) === 'won' ? 1 : 0), need: r.halls.length };
};

/** Won with the hero at the lift's landing and Dunna by it. */
export const workshopResult = (s: BattleState): 'won' | null => {
  const r = s.rules?.senda?.workshop;
  if (!r) return null;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const d = dunnaOf(s);
  if (!h || !withinRange(h.x, h.y, r.lift.x, r.lift.y, r.reach)) return null;
  return !d || withinRange(d.x, d.y, h.x, h.y, mulBp(r.reach, 25000)) ? 'won' : null;
};
